import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/config";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { CheckCircle2, Clock, CreditCard, Loader2, DollarSign } from "lucide-react";

/**
 * iter135 — Customer-side per-service Jobs view.
 * Shows every service in a booking as its own row with status +
 * per-service Pay Now button when unpaid.
 *
 * Rendered inside the customer Dashboard's BookingRow when the booking
 * has 1+ Job (which is now every booking after iter135 backfill).
 */
export default function CustomerJobsPanel({ bookingId, autopayEnabled, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [paying, setPaying] = useState("");

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/me/bookings/${bookingId}/jobs`);
      setJobs(data.jobs || []);
      setInvoice(data.invoice || null);
    } catch (e) {
      // Silent: dashboard already shows the booking summary.
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const payJob = async (job) => {
    setPaying(job.job_id);
    try {
      const { data } = await api.post(`/me/jobs/${job.job_id}/pay`);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open payment page");
    } finally {
      setPaying("");
    }
  };

  if (loading) return null;
  if (!jobs.length) return null;

  const total     = Number(invoice?.total || 0);
  const paid      = Number(invoice?.paid || 0);
  const remaining = Number(invoice?.remaining || 0);
  const anyProgress = paid > 0 || jobs.some((j) => j.status !== "pending");

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2"
         data-testid={`customer-jobs-panel-${bookingId}`}>
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-900/70">
          Services · {jobs.length}
        </div>
        {invoice && anyProgress && (
          <div className={`text-[10px] font-black uppercase tracking-wider ${
            invoice.status === "paid"    ? "text-emerald-700" :
            invoice.status === "partial" ? "text-amber-700"   :
                                           "text-slate-500"
          }`} data-testid={`customer-jobs-invoice-status-${bookingId}`}>
            {invoice.status}
          </div>
        )}
      </div>

      {jobs.map((j) => (
        <JobRow
          key={j.job_id}
          job={j}
          paying={paying === j.job_id}
          onPay={() => payJob(j)}
          autopayEnabled={autopayEnabled}
        />
      ))}

      {invoice && anyProgress && (
        <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-3 gap-1 text-center text-[10px]">
          <div>
            <div className="text-slate-500 uppercase font-bold">Total</div>
            <div className="text-sm font-black tabular-nums">${total.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-emerald-700 uppercase font-bold">Paid</div>
            <div className="text-sm font-black text-emerald-700 tabular-nums">${paid.toFixed(2)}</div>
          </div>
          <div>
            <div className={`uppercase font-bold ${remaining > 0.005 ? "text-amber-700" : "text-slate-500"}`}>Balance</div>
            <div className={`text-sm font-black tabular-nums ${remaining > 0.005 ? "text-amber-700" : "text-slate-700"}`}>
              ${remaining.toFixed(2)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function JobRow({ job, paying, onPay, autopayEnabled }) {
  const completed = job.status === "completed";
  const started   = job.status === "in_progress";
  const paid      = job.payment_status === "paid";
  const paymentPending = job.payment_status === "pending";
  const failed    = job.payment_status === "payment_failed";
  const canPay    = completed && !paid && (paymentPending || failed || job.payment_status === "unpaid");

  const statusPill = paid
    ? { label: "Paid ✅", className: "bg-emerald-100 text-emerald-800" }
    : completed
      ? (paymentPending
          ? { label: "Payment Pending", className: "bg-amber-100 text-amber-800" }
          : { label: "Payment Due", className: "bg-amber-100 text-amber-800" })
      : started
        ? { label: "In Progress", className: "bg-blue-100 text-blue-800" }
        : { label: "Scheduled", className: "bg-slate-100 text-slate-600" };

  return (
    <div className="rounded-lg bg-white border border-slate-200 p-2.5 flex items-start justify-between gap-2"
         data-testid={`customer-job-row-${job.job_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          {paid && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
          {!paid && completed && <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
          {!completed && <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          <span className="text-sm font-extrabold text-slate-900 truncate">{job.service_name}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${statusPill.className}`}
                data-testid={`customer-job-status-${job.job_id}`}>
            {statusPill.label}
          </span>
          <span className="text-[11px] font-bold tabular-nums text-slate-700">${(job.price || 0).toFixed(2)}</span>
        </div>
      </div>
      {canPay && !autopayEnabled && (
        <Button
          size="sm"
          onClick={onPay}
          disabled={paying}
          className="rounded-full h-8 px-3 text-[11px] font-black bg-amber-500 hover:bg-amber-600 text-white shrink-0"
          data-testid={`customer-job-pay-${job.job_id}`}
        >
          {paying
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <><CreditCard className="w-3.5 h-3.5 mr-1" /> Pay Now</>}
        </Button>
      )}
      {canPay && autopayEnabled && paymentPending && (
        <span className="text-[10px] font-bold text-emerald-700 shrink-0 flex items-center gap-1">
          <DollarSign className="w-3 h-3" /> AutoPay
        </span>
      )}
    </div>
  );
}
